import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as R from 'ramda';

import { useHttp, useAccount } from 'hooks';
import { fetchContributionActions } from 'actions/contributions';
import { downloadFileByLink } from 'utils/utils';
import { UserRoles } from 'helpers/constants';
import * as contentService from 'services/content.service';
import * as contributionService from 'services/contributions.service';
/**
 * Max size limit on backend is 5GB
 */
const MAX_FILES_SIZE_IN_BYTES = 5368706371;

const isFilesSizeLimitOrLess = R.compose(sum => sum <= MAX_FILES_SIZE_IN_BYTES, R.sum, R.map(R.prop('size')));

export const useSessionTimeAttachments = (sessionTimeId, contributionId) => {
  const dispatch = useDispatch();
  const { request, loading } = useHttp();
  const { currentRole } = useAccount();
  const [isFileUpload, setIsFileUpload] = useState(false);
  const [progress, setProgress] = useState(null);
  const handleIsFileLoad = () => {
    setIsFileUpload(false);
    setProgress(null);
  };

  const handleUpdateProgress = (partNumber, totalParts) => progressData => {
    const percentPerChunk = 100 / totalParts;
    setProgress((progressData / 100) * percentPerChunk + percentPerChunk * (partNumber - 1));
  };

  const guid = () => {
    var d = new Date().getTime();
    var guid = 'xxxx-xxxx-xxxx-xxxx'.replace(/[xy]/g, function (c) {
      var r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      return (c == 'x' ? r : (r & 0x7) | 0x8).toString(16);
    });
    return guid;
  };

  const loadVideo = file =>
    new Promise((resolve, reject) => {
      try {
        let video = document.createElement('video');
        video.preload = 'metadata';

        video.onloadedmetadata = function () {
          resolve(this);
        };

        video.onerror = function () {
          reject('Invalid video. Please select a video file.');
        };

        video.src = window.URL.createObjectURL(file);
      } catch (e) {
        reject(e);
      }
    });

  const handleUploadFile = useCallback(
    async ({ target: { files } }) => {
      const video = await loadVideo(files[0]);
      const chunkSize = 26214400;
      if (isFilesSizeLimitOrLess(files)) {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const documentId = guid();
          const fileName = file.name;
          let uploadId = '';
          let prevETags = '';
          let partNumber = 1;
          const roomId = guid();
          for (let start = 0; start < file.size; start += chunkSize) {
            const isLastPart = start + chunkSize >= file.size;
            const chunk = file.slice(start, start + chunkSize);
            const totalParts = Math.ceil(file.size / chunkSize);
            const result = await uploadPartFile(
              chunk,
              contributionId,
              sessionTimeId,
              partNumber,
              totalParts,
              isLastPart,
              documentId,
              fileName,
              uploadId,
              prevETags,
              roomId,
              video.duration.toFixed(),
            );
            if (result) {
              if (isLastPart) {
                dispatch(
                  await contributionService
                    .getCohealerContribution(contributionId)
                    .then(fetchContributionActions.success),
                );
              }
              uploadId = result.uploadId;
              prevETags = result.prevETags;
            }
            partNumber++;
          }
        }
      }
    },
    [contributionId, dispatch, request, sessionTimeId],
  );

  const uploadPartFile = (
    chunk,
    contributionId,
    sessionTimeId,
    partNumber,
    totalParts,
    isLastPart,
    documentId,
    fileName,
    uploadId,
    prevETags,
    roomId,
    duration,
  ) =>
    new Promise((resolve, reject) => {
      const formData = new FormData();

      formData.append('file', chunk);
      setIsFileUpload(true);
      contentService
        .addFileToSessionTime(
          formData,
          contributionId,
          sessionTimeId,
          partNumber,
          isLastPart,
          documentId,
          fileName,
          uploadId,
          prevETags,
          handleUpdateProgress(partNumber, totalParts),
          roomId,
          duration,
        )
        .then(data => {
          resolve(data);
        })
        .then(() => {
          if (isLastPart) {
            handleIsFileLoad();
          }
        })
        .catch(data => {
          handleIsFileLoad();
          console.dir(data);
          reject(data);
        });
    });

  return {
    isFileUpload,
    progress,
    loading,
    isCoach: currentRole === UserRoles.cohealer,
    handleUploadFile,
  };
};
